import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Progress } from '../ui/progress';
import { 
  Download, 
  Upload, 
  RefreshCw, 
  Database, 
  AlertTriangle,
  CheckCircle,
  FileJson,
  Trash2,
  Shield,
  Clock,
  HardDrive
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Authorization': `Bearer ${token}`
  };
};

const BackupRestore = () => {
  const [loading, setLoading] = useState(false);
  const [backupPreview, setBackupPreview] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [lastBackupInfo, setLastBackupInfo] = useState(null);
  const [operation, setOperation] = useState(null); // 'backup', 'restore', 'clear-restore'

  // Load backup preview on mount
  const loadBackupPreview = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/backup/preview`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackupPreview(data);
      }
    } catch (error) {
      console.error('Error loading backup preview:', error);
    }
  }, []);

  useEffect(() => {
    loadBackupPreview();
  }, [loadBackupPreview]);

  // Download backup
  const handleBackup = async () => {
    setLoading(true);
    setOperation('backup');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/backup`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Backup failed');
      }
      
      // Get the blob and create download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Extract filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `edulegalhub_backup_${new Date().toISOString().split('T')[0]}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setLastBackupInfo({
        date: new Date().toISOString(),
        filename: filename,
        collections: backupPreview?.collections?.length || 0,
        documents: backupPreview?.total_documents || 0
      });
      
      toast.success('Backup downloaded successfully!');
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Failed to create backup');
    } finally {
      setLoading(false);
      setOperation(null);
    }
  };

  // Handle file selection for restore
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON backup file');
      return;
    }
    
    setRestoreFile(file);
    
    // Preview the backup file
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.data || !data.backup_info) {
        toast.error('Invalid backup file format');
        setRestoreFile(null);
        return;
      }
      
      setRestorePreview({
        info: data.backup_info,
        collections: Object.entries(data.data).map(([name, docs]) => ({
          name,
          count: docs.length
        })),
        totalDocuments: Object.values(data.data).reduce((sum, docs) => sum + docs.length, 0)
      });
      
      toast.success('Backup file loaded - ready to restore');
    } catch (error) {
      console.error('Error parsing backup file:', error);
      toast.error('Failed to parse backup file');
      setRestoreFile(null);
      setRestorePreview(null);
    }
  };

  // Restore from backup (merge mode - skip duplicates)
  const handleRestore = async () => {
    if (!restoreFile) {
      toast.error('Please select a backup file first');
      return;
    }
    
    setLoading(true);
    setOperation('restore');
    
    try {
      const text = await restoreFile.text();
      
      const response = await fetch(`${API_URL}/api/admin/restore`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: text
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Restore failed');
      }
      
      const result = await response.json();
      
      toast.success(`Restore completed! ${result.summary.collections_restored} collections restored.`);
      
      // Refresh backup preview
      loadBackupPreview();
      
      // Clear restore state
      setRestoreFile(null);
      setRestorePreview(null);
      
    } catch (error) {
      console.error('Restore error:', error);
      toast.error(error.message || 'Failed to restore backup');
    } finally {
      setLoading(false);
      setOperation(null);
    }
  };

  // Clear and restore (destructive mode)
  const handleClearAndRestore = async () => {
    if (!restoreFile) {
      toast.error('Please select a backup file first');
      return;
    }
    
    // Double confirmation for destructive operation
    const confirmed = window.confirm(
      '⚠️ WARNING: This will DELETE ALL existing data and replace it with the backup!\n\n' +
      'This action cannot be undone. Are you absolutely sure?'
    );
    
    if (!confirmed) return;
    
    const doubleConfirmed = window.confirm(
      'FINAL CONFIRMATION: All current data will be permanently deleted.\n\n' +
      'Type "yes" mentally and click OK to proceed.'
    );
    
    if (!doubleConfirmed) return;
    
    setLoading(true);
    setOperation('clear-restore');
    
    try {
      const text = await restoreFile.text();
      
      const response = await fetch(`${API_URL}/api/admin/restore/clear-and-restore`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: text
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Clear and restore failed');
      }
      
      const result = await response.json();
      
      toast.success('Data cleared and restored successfully!');
      
      // Refresh backup preview
      loadBackupPreview();
      
      // Clear restore state
      setRestoreFile(null);
      setRestorePreview(null);
      
    } catch (error) {
      console.error('Clear and restore error:', error);
      toast.error(error.message || 'Failed to clear and restore');
    } finally {
      setLoading(false);
      setOperation(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Backup & Restore</h2>
          <p className="text-gray-600 mt-1">
            Backup your data before updates and restore after redeployment
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadBackupPreview}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Important Notice */}
      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Before Redeployment</AlertTitle>
        <AlertDescription className="text-blue-700">
          Always download a backup before updating your code. After redeployment, 
          use the restore feature to bring back your data and settings.
        </AlertDescription>
      </Alert>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Backup Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-green-600" />
              Create Backup
            </CardTitle>
            <CardDescription>
              Download all your data as a JSON file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Data Preview */}
            {backupPreview && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Current Database</span>
                  <span className="text-sm text-gray-500">
                    {backupPreview.total_documents} total documents
                  </span>
                </div>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {backupPreview.collections?.map((coll) => (
                    <div 
                      key={coll.name}
                      className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0"
                    >
                      <span className="text-gray-600 flex items-center gap-2">
                        <Database className="h-3 w-3" />
                        {coll.name.replace(/_/g, ' ')}
                      </span>
                      <span className="font-medium text-gray-900">{coll.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Last Backup Info */}
            {lastBackupInfo && (
              <div className="bg-green-50 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-4 w-4" />
                  <span>Last backup: {new Date(lastBackupInfo.date).toLocaleString()}</span>
                </div>
              </div>
            )}

            <Button 
              onClick={handleBackup}
              disabled={loading || !backupPreview?.total_documents}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {loading && operation === 'backup' ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creating Backup...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download Backup
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Restore Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Restore from Backup
            </CardTitle>
            <CardDescription>
              Upload a backup file to restore your data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
                id="backup-file-input"
                disabled={loading}
              />
              <label 
                htmlFor="backup-file-input"
                className="cursor-pointer"
              >
                <FileJson className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                <p className="text-sm text-gray-600">
                  {restoreFile ? restoreFile.name : 'Click to select backup file'}
                </p>
                <p className="text-xs text-gray-500 mt-1">JSON backup files only</p>
              </label>
            </div>

            {/* Restore Preview */}
            {restorePreview && (
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800">Backup Contents</span>
                  <span className="text-xs text-blue-600 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(restorePreview.info.created_at).toLocaleString()}
                  </span>
                </div>
                
                <div className="text-sm text-blue-700">
                  <span className="font-medium">{restorePreview.totalDocuments}</span> documents in{' '}
                  <span className="font-medium">{restorePreview.collections.length}</span> collections
                </div>

                <div className="space-y-1 max-h-32 overflow-y-auto text-sm">
                  {restorePreview.collections.map((coll) => (
                    <div key={coll.name} className="flex justify-between text-blue-600">
                      <span>{coll.name.replace(/_/g, ' ')}</span>
                      <span>{coll.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Restore Buttons */}
            <div className="space-y-2">
              <Button 
                onClick={handleRestore}
                disabled={loading || !restoreFile}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading && operation === 'restore' ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Restore (Merge - Skip Duplicates)
                  </>
                )}
              </Button>

              <Button 
                onClick={handleClearAndRestore}
                disabled={loading || !restoreFile}
                variant="destructive"
                className="w-full"
              >
                {loading && operation === 'clear-restore' ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Clearing & Restoring...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All & Restore (Destructive)
                  </>
                )}
              </Button>
            </div>

            {/* Warning for destructive restore */}
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Destructive Restore Warning</AlertTitle>
              <AlertDescription className="text-xs">
                "Clear All & Restore" will permanently delete all existing data before restoring. 
                Use "Merge" mode to safely add new data without deleting existing records.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>

      {/* Usage Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            How to Use
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Before Redeployment:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                <li>Click "Download Backup" to save all your data</li>
                <li>Keep the JSON file safe on your computer</li>
                <li>Proceed with code updates and redeployment</li>
              </ol>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">After Redeployment:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                <li>Login to admin dashboard on the new deployment</li>
                <li>Go to Backup & Restore</li>
                <li>Upload your backup file</li>
                <li>Click "Restore" to bring back your data</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupRestore;
